import { isFunction, cloneDeep, isNumber, isArray } from 'lodash';

export default class Matrix {
  constructor(data, width, height) {
    this.width = width;
    this.height = height;
    this.data = data;
    this.transposed = [];
    this.rows = [];
    this.object = null;
    this.mapRow = [];
    this.mapCell = 0;
  }

  transpose() {
    this.transposed = cloneDeep(this.data);
    this.data = Matrix.transpose(this.data);
  }

  setMatrixValues({ w, object }) {
    this.width = w;
    this.data = new Array(w).fill(null).map((_, i) => {
      const row = isFunction(this.mapRow) ? this.mapRow(object[i], i) : [];
      if (isArray(row)) {
        return row.map((rowVal) => {
          const cell = isFunction(this.mapCell) ? this.mapCell(rowVal, i) : rowVal;
          return cell;
        });
      }
      throw new Error('Row should be array!');
    });
    this.transposed = Matrix.transpose(this.data);
  }

  addRow(value = null) {
    const { height } = this;

    this.data.push(new Array(height).fill(value));

    return this;
  }

  setRow(i, fn) {
    if (!isNumber(i)) throw new Error('Row index is required to change row value');

    this.rows[i] = fn(cloneDeep(this.rows[i]));
    return this;
  }

  addColumn(value = null) {
    this.data = this.data.map((row) => [...row, value]);
    return this;
  }

  removeRow(index) {
    this.data = this.data.filter((_, i) => index !== i);

    return this;
  }

  setObject(i, fn) {
    this.object = this.object.map((obj, index) => {
      if (index === i) {
        return fn(obj);
      }
      return obj;
    });
    return this;
  }

  set(i, j, value) {
    this.data[i][j] = value;

    return this;
  }

  get(i, j) {
    return this.data[i][j];
  }

  getColumn(i) {
    const { data } = this;
    return data?.map((row) => row[i]) || [];
  }

  forEachColumn(fn) {
    const { transposed } = this;
    if (!isFunction(fn)) return;
    transposed.forEach(fn);
  }

  reduceEachColumn(fn, initialValue = 0) {
    const { transposed } = this;
    if (!isFunction(fn)) return;
    return transposed.map((column) => column.reduce(fn, initialValue));
  }

  reduceEachRow(fn, initialValue = 0) {
    const { data } = this;
    if (!isFunction(fn)) return;
    return data?.map((row) => row.reduce(fn, initialValue));
  }

  cloneDeep() {
    return Matrix.create({
      w: this.width,
      h: this.height,
      data: cloneDeep(this.data),
      rows: cloneDeep(this.rows),
      object: cloneDeep(this.object),
    });
  }

  static transpose(matrix) {
    return matrix[0]?.map((_, colIndex) => matrix.map((row) => row[colIndex])) || [];
  }

  static create({ w, h, fill, data, rows, object, mapRow, mapCell }) {
    const matrix = new Matrix();
    matrix.object = object;
    matrix.rows = rows;
    matrix.mapRow = mapRow;
    matrix.mapCell = mapCell;
    matrix.setMatrixValues({ w, h, fill, rows, mapRow, mapCell, object });

    if (data) {
      matrix.data = data;
      matrix.transposed = matrix.data?.length > 0 ? Matrix.transpose(data) : [];
    }

    return matrix;
  }
}
